import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UpdateNotificationSettingsRequest, NotificationSettings } from '@property/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast, showMutationErrorToast } from '@/lib/toast';
import { captureMutationError } from '@/lib/sentry';
import { useAuthQuery } from './use-auth-query';

export function useNotificationSettings() {
  return useAuthQuery({
    queryKey: queryKeys.notificationSettings.mine(),
    queryFn: api.getNotificationSettings,
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateNotificationSettingsRequest) =>
      api.updateNotificationSettings(request),

    onSuccess: (settings: NotificationSettings) => {
      queryClient.setQueryData(queryKeys.notificationSettings.mine(), settings);
    },

    onError: (error) => {
      captureMutationError(error, { mutation: 'useUpdateNotificationSettings' });
      showMutationErrorToast(error, 'Failed to save notification settings. Please try again.');
    },
  });
}

export function useSendWhatsAppOtp() {
  return useMutation({
    mutationFn: (phoneNumber: string) => api.sendWhatsAppOtp(phoneNumber),

    onError: (error) => {
      captureMutationError(error, { mutation: 'useSendWhatsAppOtp' });
      showMutationErrorToast(error, 'Failed to send the verification code. Please try again.');
    },
  });
}

export function useVerifyWhatsAppOtp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ phoneNumber, code }: { phoneNumber: string; code: string }) =>
      api.verifyWhatsAppOtp(phoneNumber, code),

    onSuccess: (settings: NotificationSettings) => {
      queryClient.setQueryData(queryKeys.notificationSettings.mine(), settings);
      toast.success('WhatsApp number verified.');
    },

    onError: (error) => {
      captureMutationError(error, { mutation: 'useVerifyWhatsAppOtp' });
      showMutationErrorToast(error, 'Invalid or expired code. Please try again.');
    },
  });
}

export function useRemoveWhatsAppNumber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.removeWhatsAppNumber(),

    onSuccess: (settings: NotificationSettings) => {
      queryClient.setQueryData(queryKeys.notificationSettings.mine(), settings);
      toast.success('WhatsApp number removed.');
    },

    onError: (error) => {
      captureMutationError(error, { mutation: 'useRemoveWhatsAppNumber' });
      showMutationErrorToast(error, 'Failed to remove the number. Please try again.');
    },
  });
}
